// WaysToUseModal — the slim per-kind "Unique ways to use…" pop-up rendered
// from each pill in the CreateSiloDialog Step-0 picker. Same visual language
// as UniqueWaysModal (marketing page), but takes an ideas array + title +
// subtitle so one component serves all four pill kinds. (Randy, Jul 2026)
import { useEffect, useRef, useState } from "react";
import { X, CaretDown } from "@phosphor-icons/react";

export default function WaysToUseModal({ open, onClose, title, subtitle, ideas = [], kind = "generic" }) {
  const [openIdx, setOpenIdx] = useState(ideas.length === 1 ? 0 : null);
  const itemRefs = useRef([]);

  useEffect(() => {
    if (!open) return;
    setOpenIdx(ideas.length === 1 ? 0 : null);
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, ideas.length, onClose]);

  if (!open) return null;

  const toggle = (i) => {
    setOpenIdx((prev) => {
      const next = prev === i ? null : i;
      if (next !== null) {
        setTimeout(() => {
          itemRefs.current[next]?.scrollIntoView({ block: "start", behavior: "smooth" });
        }, 40);
      }
      return next;
    });
  };

  return (
    <div
      className="fixed inset-0 z-[95] flex items-center justify-center p-4"
      onClick={onClose}
      data-testid={`ways-to-use-modal-${kind}`}
    >
      <div className="absolute inset-0 bg-[#1C1B1A]/55 backdrop-blur-sm" />
      <div
        className="relative z-10 bg-[#FDFCF8] rounded-2xl max-w-lg w-full max-h-[88dvh] flex flex-col shadow-2xl border hairline"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 px-6 sm:px-7 pt-6 pb-4 shrink-0 border-b hairline">
          <div>
            <h3 className="font-serif-display text-2xl text-[#1C1B1A] leading-tight">
              {title}
            </h3>
            <p className="text-[13px] text-[#6B6A68] mt-1">
              {subtitle || "Tap any idea to see how a digital twin brings it to life."}
            </p>
          </div>
          <button
            onClick={onClose}
            data-testid={`ways-to-use-close-${kind}`}
            className="text-[#6B6A68] hover:text-[#1C1B1A] p-1 shrink-0"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 px-3 sm:px-4 py-3">
          {ideas.map((w, i) => {
            const open2 = openIdx === i;
            return (
              <div
                key={i}
                ref={(el) => { itemRefs.current[i] = el; }}
                className={`rounded-xl mb-1.5 transition-colors ${open2 ? "bg-white border hairline shadow-sm" : "hover:bg-white/60"}`}
              >
                <button
                  type="button"
                  onClick={() => toggle(i)}
                  data-testid={`ways-to-use-item-${kind}-${i}`}
                  aria-expanded={open2}
                  className="w-full flex items-center justify-between gap-3 text-left px-4 py-3"
                >
                  <span className={`text-[15px] ${open2 ? "font-semibold text-[#1C1B1A]" : "font-medium text-[#3A3A38]"}`}>
                    {w.title}
                  </span>
                  <CaretDown
                    size={16}
                    weight="bold"
                    className={`shrink-0 text-[#4A5D4E] transition-transform duration-200 ${open2 ? "rotate-180" : ""}`}
                  />
                </button>
                {open2 && (
                  <p
                    data-testid={`ways-to-use-body-${kind}-${i}`}
                    className="px-5 sm:px-7 pb-5 pt-1 text-[13.5px] leading-relaxed text-[#4A4A48] text-center whitespace-pre-line"
                  >
                    {w.body}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
